import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Tag, ThumbsUp, Languages, PencilLine, Inbox, CheckCircle2, Headset, Mail, Store, ChevronDown } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { CATEGORY_ICONS, CATEGORIES, PREVIEW_ACTIVITIES, FULL_ACTIVITIES, SUPPORT_EMAIL } from "@/lib/constants";
import Logo from "@/components/Logo";

export default function Landing() {
  const { t, tCat, tAct, lang, setLang, dir } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;
  const [expanded, setExpanded] = React.useState(null);

  React.useEffect(() => {
    if (user) {
      if (user.role === "customer") navigate("/customer", { replace: true });
      else if (user.role === "merchant") navigate("/merchant", { replace: true });
      else if (user.role === "admin") navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="page-shell" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-zinc-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 py-3">
          <Logo className="h-9 w-auto" testId="landing-logo" />
          <div className="flex items-center gap-2">
            <button
              data-testid="lang-toggle-landing"
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              aria-label={t("changeLanguage")}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-zinc-100 hover:bg-zinc-200 text-xs font-semibold text-zinc-800 transition"
            >
              <Languages className="h-3.5 w-3.5" aria-hidden="true" />
              {lang === "en" ? "ع" : "EN"}
            </button>
            <Link to="/login" data-testid="header-login" className="h-9 px-4 inline-flex items-center rounded-full border border-emerald-600 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition">
              {t("login")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/80 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 pt-12 pb-10 sm:pt-16 relative">
          <div className="max-w-2xl rise">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100/70 text-emerald-800 px-4 py-1.5 text-xs font-bold mb-6" data-testid="hero-slogan-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {t("slogan1")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.12] font-extrabold text-[#12211D] tracking-tight" data-testid="hero-title">
              {t("tagline")}
            </h1>
            <p className="mt-5 text-zinc-600 text-base leading-relaxed max-w-xl" data-testid="hero-sub">{t("sub")}</p>
            <p className="mt-3 text-emerald-700 font-bold text-base" data-testid="hero-slogan2">{t("slogan2")}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/signup?role=customer" data-testid="cta-customer" className="btn-primary sm:min-w-[200px]">
                {t("iAmCustomer")} <Arrow className="h-4 w-4" />
              </Link>
              <Link to="/signup?role=merchant" data-testid="cta-merchant" className="btn-secondary sm:min-w-[200px]">
                <Store className="h-4 w-4" /> {t("iAmMerchant")}
              </Link>
            </div>
            <Link to="/login" data-testid="cta-login" className="inline-block text-sm text-zinc-600 hover:text-zinc-900 mt-4">
              {t("alreadyHave")} <span className="font-semibold text-emerald-700 underline-offset-4 hover:underline">{t("login")}</span>
            </Link>
          </div>

          {/* Trust strip */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 rise rise-2" data-testid="trust-strip">
            {[
              { icon: ShieldCheck, title: t("trustPoint1"), sub: t("trustPoint1Sub") },
              { icon: Tag, title: t("trustPoint2"), sub: t("trustPoint2Sub") },
              { icon: ThumbsUp, title: t("trustPoint3"), sub: t("trustPoint3Sub") },
            ].map((s, i) => (
              <div key={i} className="card-surface p-4 flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-bold text-zinc-900 text-sm">{s.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-6xl mx-auto px-5 mt-10" data-testid="categories-section">
        <h2 className="font-display text-lg font-extrabold text-[#12211D]">{t("browseCategories")}</h2>
        {/* General banner — shown once above the categories */}
        <div className="mt-3 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-900 text-sm font-medium px-4 py-3" data-testid="categories-banner">
          {t("categoriesBanner")}
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {CATEGORIES.map((cat) => {
            const Icon = CATEGORY_ICONS[cat];
            const preview = PREVIEW_ACTIVITIES[cat] || [];
            const full = FULL_ACTIVITIES[cat] || [];
            const isOpen = expanded === cat;
            const sep = lang === "ar" ? "، " : ", ";
            return (
              <div key={cat} data-testid={`category-card-${cat}`} className="card-surface p-4">
                <button
                  type="button"
                  data-testid={`category-expand-${cat}`}
                  onClick={() => setExpanded(isOpen ? null : cat)}
                  aria-expanded={isOpen}
                  aria-label={tCat(cat)}
                  className="w-full flex items-start gap-3 text-start"
                >
                  <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
                    <Icon className="h-5 w-5" strokeWidth={2.1} aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-zinc-900 text-sm">{tCat(cat)}</span>
                      <ChevronDown className={`h-4 w-4 text-zinc-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{preview.map(tAct).join(sep)}</p>
                    {!isOpen && (
                      <span className="inline-block mt-1.5 text-[11px] font-semibold text-emerald-700">
                        {t("viewAllActivities")} ({full.length})
                      </span>
                    )}
                  </div>
                </button>
                {isOpen && (
                  <div className="mt-3 pt-3 border-t border-zinc-100" data-testid={`category-full-${cat}`}>
                    <div className="flex flex-wrap gap-1.5">
                      {full.map((a) => (
                        <span key={a} className="rounded-full bg-zinc-50 border border-zinc-200 px-2.5 py-1 text-[11px] text-zinc-700">
                          {tAct(a)}
                        </span>
                      ))}
                    </div>
                    {cat === "pets" && (
                      <p className="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-medium px-3 py-2" data-testid="vet-notice-landing">
                        {t("vetNotice")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 mt-14" data-testid="how-it-works">
        <h2 className="font-display text-lg font-extrabold text-[#12211D]">{t("howItWorks")}</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { icon: PencilLine, title: t("step1Title"), desc: t("step1Desc") },
            { icon: Inbox, title: t("step2Title"), desc: t("step2Desc") },
            { icon: CheckCircle2, title: t("step3Title"), desc: t("step3Desc") },
          ].map((s, i) => (
            <div key={i} className="card-surface p-5">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-emerald-600 text-white grid place-items-center shrink-0">
                  <s.icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">{i + 1}</span>
              </div>
              <div className="font-bold text-zinc-900 mt-3">{s.title}</div>
              <div className="text-sm text-zinc-600 mt-1 leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust + Support band */}
      <section className="mt-14 band-deep text-white" data-testid="trust-support-band">
        <div className="max-w-6xl mx-auto px-5 py-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div className="h-12 w-12 rounded-2xl bg-white/10 grid place-items-center mb-4">
              <ShieldCheck className="h-6 w-6 text-emerald-300" />
            </div>
            <h3 className="font-display text-xl font-extrabold">{t("trustTitle")}</h3>
            <p className="mt-2 text-emerald-100/80 text-sm leading-relaxed max-w-md">{t("trustBody")}</p>
          </div>
          <div>
            <div className="h-12 w-12 rounded-2xl bg-white/10 grid place-items-center mb-4">
              <Headset className="h-6 w-6 text-emerald-300" />
            </div>
            <h3 className="font-display text-xl font-extrabold">{t("supportTitle")}</h3>
            <p className="mt-2 text-emerald-100/80 text-sm leading-relaxed">{t("supportBody")}</p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="support-email-link"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-emerald-900 px-5 py-2.5 text-sm font-bold hover:bg-emerald-50 transition"
            >
              <Mail className="h-4 w-4" /> {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-zinc-200" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-5 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Logo className="h-8 w-auto" testId="footer-logo" />
            <div className="flex items-center gap-4 text-xs text-zinc-600">
              <Link to="/terms" data-testid="footer-terms" className="hover:text-emerald-700 hover:underline underline-offset-4">
                {dir === "rtl" ? "الشروط والأحكام" : "Terms"}
              </Link>
              <span className="text-zinc-300">·</span>
              <Link to="/privacy" data-testid="footer-privacy" className="hover:text-emerald-700 hover:underline underline-offset-4">
                {dir === "rtl" ? "سياسة الخصوصية" : "Privacy"}
              </Link>
              <span className="text-zinc-300">·</span>
              <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="footer-support" className="hover:text-emerald-700 hover:underline underline-offset-4">
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-zinc-100 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[11px] text-zinc-400">© {new Date().getFullYear()} Hustlers KSA — {t("allRights")}</p>
            <p className="text-[11px] font-bold text-emerald-700">{t("slogan1")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
